import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useApp } from "@/contexts/AppContext";
import { getEventId, getEventPoster, applyPosterFallback } from "@/lib/eventDisplay";
import { CalendarDays, ExternalLink, MapPin, Search, Sparkles, Ticket } from "lucide-react";

const HERO_IMAGE = "https://images.unsplash.com/photo-1567879832585-fb91c9ac4e09?w=1800&q=85";
const DEFAULT_SEASONAL_CAMPAIGN = {
  key: "navratri-2026",
  label: "Navratri 2026",
  title: "Garba, Dandiya and Raas nights across the UK",
  subtitle: "Find verified community events, ticketed nights and external listings in one place.",
};

export const NAVRATRI_CONFIG = {
  campaignKey: "navratri-2026",
  badge: "Navratri",
  heroImage: HERO_IMAGE,
  endpoint: "/navratri/events",
  loadingLabel: "Loading Navratri nights...",
  emptyTitle: "No Navratri listings found",
  upcomingTitle: "Upcoming Navratri nights",
  fallbackCampaign: DEFAULT_SEASONAL_CAMPAIGN,
  useSeasonalOption: true,
};

const dateKey = (value) => value ? String(value).slice(0, 10) : "";

function isTonight(event) {
  return dateKey(event.date) === new Date().toISOString().slice(0, 10);
}

function isThisWeekend(event) {
  const raw = dateKey(event.date);
  if (!raw) return false;
  const d = new Date(`${raw}T12:00:00`);
  const now = new Date();
  const end = new Date(now);
  end.setDate(now.getDate() + 7);
  return d >= new Date(now.toDateString()) && d <= end && [5, 6, 0].includes(d.getDay());
}

function lowestPrice(event) {
  const prices = event.prices || [];
  const values = prices.map((item) => Number(item.price)).filter((value) => Number.isFinite(value));
  if (Number.isFinite(Number(event.price_from))) values.push(Number(event.price_from));
  if (values.length === 0) return null;
  return Math.min(...values);
}

export function FestivalCampaignPage({ config = NAVRATRI_CONFIG }) {
  const navigate = useNavigate();
  const { cities } = useApp();
  const [events, setEvents] = useState([]);
  const [query, setQuery] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("All locations");
  const [loading, setLoading] = useState(true);
  const [seasonalCampaign, setSeasonalCampaign] = useState(config.fallbackCampaign);

  useEffect(() => {
    api.get(config.useSeasonalOption ? "/app-options/seasonal_campaign" : "/app-options/event_campaigns")
      .then((r) => {
        if (config.useSeasonalOption && r.data?.value) {
          setSeasonalCampaign({ ...config.fallbackCampaign, ...r.data.value });
          return;
        }
        const campaign = (r.data?.value || []).find((item) => item.key === config.campaignKey);
        if (campaign) {
          setSeasonalCampaign({ ...config.fallbackCampaign, ...campaign, subtitle: campaign.description || config.fallbackCampaign.subtitle });
        }
      })
      .catch(() => {});
  }, [config]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedLocation !== "All locations") params.set("city", selectedLocation);
    setLoading(true);
    api.get(`${config.endpoint}?${params.toString()}`)
      .then((r) => setEvents(Array.isArray(r.data) ? r.data : []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [selectedLocation, config.endpoint]);

  const locations = useMemo(
    () => ["All locations", ...new Set((cities || []).filter((item) => item && item !== "All UK" && item !== "All locations"))],
    [cities],
  );

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return events;
    return events.filter((event) => [
      event.title,
      event.venue,
      event.city,
      event.language,
      event.description,
    ].some((value) => String(value || "").toLowerCase().includes(needle)));
  }, [events, query]);

  const tonight = filtered.filter(isTonight);
  const weekend = filtered.filter((event) => !isTonight(event) && isThisWeekend(event));
  const upcoming = filtered.filter((event) => !isTonight(event) && !isThisWeekend(event));

  const openEvent = (event) => {
    const eventId = getEventId(event);
    if (event.source_type === "external" && event.url) {
      window.open(event.url, "_blank", "noopener,noreferrer");
      return;
    }
    if (eventId) navigate(`/event/${eventId}`);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <section className="relative min-h-[430px] overflow-hidden border-b border-border">
        <img src={config.heroImage} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative page-shell flex min-h-[430px] flex-col justify-end py-10 text-white">
          <div className="max-w-4xl">
            <p className="text-xs font-black uppercase tracking-[0.35em] text-primary">{seasonalCampaign.label || config.fallbackCampaign.label}</p>
            <h1 className="mt-3 font-display text-4xl font-black leading-tight sm:text-5xl lg:text-6xl">
              {seasonalCampaign.title || config.fallbackCampaign.title}
            </h1>
            <p className="mt-4 max-w-2xl text-sm text-white/80 md:text-base">
              {seasonalCampaign.subtitle || config.fallbackCampaign.subtitle}
            </p>
          </div>
          <div className="mt-8 grid gap-3 rounded-lg border border-white/20 bg-black/35 p-3 backdrop-blur md:grid-cols-[1fr_auto]">
            <label className="relative block">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/60" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by event, venue or area"
                className="h-11 w-full rounded-md border border-white/20 bg-white/10 pl-10 pr-3 text-sm text-white placeholder:text-white/60 outline-none focus:border-primary"
              />
            </label>
            <select
              aria-label="Filter by location"
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              className="h-11 rounded-md border border-white/20 bg-white/10 px-3 text-sm font-bold text-white outline-none focus:border-primary"
            >
              {locations.map((item) => <option key={item} value={item} className="text-black">{item}</option>)}
            </select>
          </div>
        </div>
      </section>

      <main className="page-shell py-10">
        <div className="grid gap-3 sm:grid-cols-3">
          <Stat label="Events found" value={loading ? "..." : filtered.length} />
          <Stat label="Tonight" value={loading ? "..." : tonight.length} />
          <Stat label="This weekend" value={loading ? "..." : weekend.length} />
        </div>

        {loading ? (
          <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 6 }).map((_, index) => <div key={index} className="h-72 rounded-lg border border-border bg-muted/40" />)}
          </div>
        ) : filtered.length === 0 ? (
          <section className="mt-8 rounded-lg border border-dashed border-border p-8 text-center">
            <p className="font-display text-2xl font-black">{config.emptyTitle}</p>
            <p className="mt-2 text-sm text-muted-foreground">Try another location or search term.</p>
          </section>
        ) : (
          <div className="mt-8 space-y-12">
            {tonight.length > 0 && <EventSection title="Tonight" events={tonight} onOpen={openEvent} badge={config.badge} />}
            {weekend.length > 0 && <EventSection title="This weekend" events={weekend} onOpen={openEvent} badge={config.badge} />}
            {upcoming.length > 0 && <EventSection title={config.upcomingTitle} events={upcoming} onOpen={openEvent} badge={config.badge} />}
          </div>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <p className="font-display text-3xl font-black">{value}</p>
      <p className="mt-1 text-xs font-black uppercase tracking-[0.18em] text-muted-foreground">{label}</p>
    </div>
  );
}

function EventSection({ title, events, onOpen, badge }) {
  return (
    <section>
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="font-display text-2xl font-black">{title}</h2>
        <span className="text-sm font-bold text-muted-foreground">{events.length} events</span>
      </div>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {events.map((event) => <FestivalCard key={getEventId(event) || event.eventbrite_id || event.title} event={event} onOpen={onOpen} badge={badge} />)}
      </div>
    </section>
  );
}

function FestivalCard({ event, onOpen, badge }) {
  const poster = getEventPoster(event);
  const price = lowestPrice(event);
  const external = event.source_type === "external";
  return (
    <article className="overflow-hidden rounded-lg border border-border bg-card transition hover:-translate-y-0.5 hover:border-primary/60">
      <button type="button" onClick={() => onOpen(event)} className="block w-full text-left">
        <div className="relative aspect-[16/10] overflow-hidden bg-muted">
          <img src={poster} alt="" loading="lazy" decoding="async" onError={(e) => applyPosterFallback(e, event)} className="h-full w-full object-cover transition duration-500 hover:scale-105" />
          <div className="absolute left-3 top-3 flex flex-wrap gap-2">
            <Badge className="bg-primary text-primary-foreground"><Sparkles className="mr-1 h-3 w-3" /> {badge}</Badge>
            {external && <Badge variant="secondary">External</Badge>}
          </div>
        </div>
        <div className="p-4">
          <div className="flex items-start justify-between gap-3">
            <h3 className="font-display text-xl font-black leading-tight line-clamp-2">{event.title}</h3>
            {price !== null && <span className="shrink-0 rounded-full bg-muted px-2.5 py-1 text-xs font-black">from £{price.toFixed(0)}</span>}
          </div>
          <div className="mt-3 space-y-2 text-sm text-muted-foreground">
            <p className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-primary" /> {event.date || "Date TBC"} {event.time || ""}</p>
            <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> {event.venue || "Venue TBC"}, {event.city || "UK"}</p>
          </div>
          <div className="mt-4 flex items-center justify-between gap-3">
            <span className="text-xs font-bold text-primary">{event.festival_campaign || event.language || "All languages"}</span>
            <span className="inline-flex items-center gap-1 text-sm font-black">
              {external ? <ExternalLink className="h-4 w-4" /> : <Ticket className="h-4 w-4" />}
              {external ? "Open listing" : "View tickets"}
            </span>
          </div>
        </div>
      </button>
    </article>
  );
}

export default function Navratri() {
  return <FestivalCampaignPage config={NAVRATRI_CONFIG} />;
}
